import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import dayjs from 'dayjs';
import { useShallow } from 'zustand/react/shallow';

import { useWorkInfoStore } from '~/store';
import { isSameOrAfterDate } from '~/utils';

type IntervalRef = ReturnType<typeof setInterval> | null;

/**
 * @description 1초 간격으로 퇴근 버튼 활성화 여부를 확인하는 훅
 * @remarks
 * - 출근 상태이면 1초 간격으로 퇴근 버튼 활성화 여부를 폴링
 * - 앱이 포어그라운드로 전환되면 출근 상태인지 확인하고, 즉시 폴링 재개
 * - 페이지 이탈/퇴근 시 폴링을 중단
 */
export const useCheckEnableLeaveWork = () => {
  const { status, endWorkAvailableFrom } = useWorkInfoStore(
    useShallow((state) => ({
      status: state.workInfo.status,
      endWorkAvailableFrom: state.workInfo.endWorkAvailableFrom,
    }))
  );

  const isCommute = useMemo(() => status?.code === 'START_WORK', [status]);

  const [enableLeaveWork, setEnableLeaveWork] = useState(false);
  const intervalId = useRef<IntervalRef>(null);

  /** 인터벌 중단 - 단일 포인트 */
  const stopPolling = useCallback(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = null;
    }
  }, []);

  /** 현재 시점 기준 퇴근 가능 여부 계산 */
  const computeEnableLeaveWork = useCallback(() => {
    if (!isCommute || !endWorkAvailableFrom) {
      return false;
    }

    const now = dayjs().toISOString();

    return isSameOrAfterDate(now, endWorkAvailableFrom, 'second');
  }, [isCommute, endWorkAvailableFrom]);

  /** 퇴근 버튼 활성화 여부 갱신 */
  const tick = useCallback(() => {
    const isEnableLeaveWork = computeEnableLeaveWork();
    setEnableLeaveWork(isEnableLeaveWork);
  }, [computeEnableLeaveWork]);

  /** 포그라운드 이벤트 */
  const handleAppForeground = useCallback(() => {
    tick();
  }, [tick]);

  /** 포그라운드 이벤트 핸들러 등록/해제 */
  useEffect(() => {
    window.onAppForeground = handleAppForeground;

    return () => {
      window.onAppForeground = null;
    };
  }, [handleAppForeground]);

  /**
   * 출근 상태인 경우 폴링 시작
   */
  useEffect(() => {
    if (isCommute && !intervalId.current) {
      tick();
      intervalId.current = setInterval(tick, 1000);
    } else {
      stopPolling();
    }

    return stopPolling;
  }, [isCommute, tick, stopPolling]);

  /** 언마운트 시 정리 */
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    enableLeaveWork,
    endWorkAvailableFrom,
    checkEnableLeaveWork: tick,
  };
};

export default useCheckEnableLeaveWork;
